// Home categories loading and interaction
// Configuration and retry logic

const API_BASE = window.apiConfig?.baseURL || (function(){ try { return window.location.hostname.match(/localhost|127\.0\.0\.1/) ? `${location.protocol}//${location.hostname}:3000/api/v1` : `${location.origin}/api/v1`; } catch { return `${location.protocol}//${location.hostname}/api/v1`; } })();

let businessesCache = null;

const RETRY_CONFIG = {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 10000,
    factor: 2,
    retryOn: [429, 502, 503, 504]
};

async function fetchWithRetry(url, options = {}, attempt = 1) {
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...(options.headers || {})
            }
        });

        if (response.ok || !RETRY_CONFIG.retryOn.includes(response.status)) {
            return response;
        }

        if (attempt >= RETRY_CONFIG.maxRetries) {
            throw new Error(`Máximo de reintentos (${RETRY_CONFIG.maxRetries}) alcanzado para ${url}`);
        }

        const delay = Math.min(
            RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.factor, attempt - 1),
            RETRY_CONFIG.maxDelay
        );

        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchWithRetry(url, options, attempt + 1);
        
    } catch (error) {
        throw error;
    }
}

const categoryGradients = [
    'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
    'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
    'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
    'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    'linear-gradient(135deg, #60a5fa 60%, #a5b4fc 100%)'
];

function escapeHtml(text) {
    try {
        return String(text == null ? '' : text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    } catch {
        return '';
    }
}

// Load categories when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('categorias-grid');
    if (!container) return;
    
    const hydrate = () => {
        initializeCategories(container);
        // Aleatorio: 50% categorías madre, 50% subcategorías
        if (Math.random() < 0.5) {
            loadMainCategories(container).catch(err => {/* Error al cargar categorías */});
        } else {
            loadPopularSubcategories(container).catch(err => {
                // Fallback a categorías madre si falla
                loadMainCategories(container).catch(() => {});
            });
        }
    };
    
    if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver((entries, observer) => {
            const entry = entries[0];
            if (entry && entry.isIntersecting) {
                hydrate();
                observer.disconnect();
            }
        }, { rootMargin: '200px 0px' });
        obs.observe(container);
    } else if ('requestIdleCallback' in window) {
        requestIdleCallback(hydrate);
    } else {
        window.addEventListener('load', hydrate);
    }
});

function initializeCategories(container) {
    if (!container) return;
    container.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'loading-categories';
    const sp = document.createElement('div');
    sp.className = 'spinner';
    const p = document.createElement('p');
    p.textContent = 'Cargando categorías...';
    wrap.appendChild(sp);
    wrap.appendChild(p);
    container.appendChild(wrap);
}

async function loadMainCategories(container) {
    if (!container) return;
    
    try {
        initializeCategories(container);
        
        // Usar fetchWithRetry para manejar 429 de forma resiliente
        const response = await fetchWithRetry(`${API_BASE}/public/popular-categories?limit=12`);
        if (!response.ok) throw new Error('No se pudo obtener categorías populares');
        
        const data = await response.json();
        const categories = data?.data?.categories || [];
        
        // Guardar en cache global para home-interactions.js
        if (categories.length > 0) {
            window.__popularCategoriesCache = categories;
        }

        if (!categories.length) throw new Error('No hay categorías populares disponibles');
        renderCategories(container, categories); // isSubcategory = false (default)
    } catch (error) {
        showError(container, `Error al cargar categorías: ${error.message}`);
    }
}

// Cargar subcategorías populares (50% de las veces)
async function loadPopularSubcategories(container) {
    if (!container) return;
    
    try {
        initializeCategories(container);
        
        // Fetch de negocios para contar por subcategoría
        const response = await fetchWithRetry(`${API_BASE}/businesses?limit=300`);
        if (!response.ok) throw new Error('No se pudo obtener negocios');
        
        const data = await response.json();
        const businesses = data?.data?.businesses || [];
        
        if (!businesses.length) throw new Error('No hay negocios disponibles');
        
        // Agrupar por subcategoría y contar
        const subcatMap = new Map();
        businesses.forEach(b => {
            const slug = b.subcategory || '';
            const name = b.subcategory || '';
            if (!slug) return;
            // Usar nombre legible si existe
            const displayName = name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const existing = subcatMap.get(slug);
            if (existing) {
                existing.count++;
            } else {
                subcatMap.set(slug, { name: displayName, slug, count: 1 });
            }
        });
        
        // Convertir a array y ordenar de mayor a menor
        let subcategories = Array.from(subcatMap.values());
        subcategories.sort((a, b) => b.count - a.count);
        subcategories = subcategories.slice(0, 12);
        
        if (!subcategories.length) throw new Error('No hay subcategorías disponibles');
        
        // Guardar en cache
        window.__popularCategoriesCache = subcategories;
        
        renderCategories(container, subcategories, true);
    } catch (error) {
        throw error; // Se captura en el hydrate para hacer fallback
    }
}

function formatNumber(num) {
    if (typeof num !== 'number') return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function getCategoryGradient(categoryName) {
    if (!categoryName) return categoryGradients[0];
    
    // Gradiente específico para Influencers y Creadores Digitales
    const lowerName = categoryName.toLowerCase();
    if (lowerName.includes('influencer') || lowerName.includes('creador')) {
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    }
    
    let hash = 0;
    for (let i = 0; i < categoryName.length; i++) {
        hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % categoryGradients.length;
    return categoryGradients[index];
}

function renderCategories(container, categories, isSubcategory = false) {
    if (!container || !categories || !categories.length) {
        showNoCategories(container);
        return;
    }

    const visibleCategories = categories.slice(0, 12);
    const groupedCategories = [];
    for (let i = 0; i < visibleCategories.length; i += 4) {
        groupedCategories.push(visibleCategories.slice(i, i + 4));
    }

    container.replaceChildren();
    const frag = document.createDocumentFragment();
    groupedCategories.forEach(group => {
        const groupEl = document.createElement('div');
        groupEl.className = 'categoria-group categoria-group-4';
        group.forEach(category => {
            if (!category) return;

            const icon = getCategoryIcon(category.name || '');
            const gradient = getCategoryGradient(category.name || '');
            const countNum = Number(category.count);
            const count = formatNumber(Number.isFinite(countNum) ? countNum : 0);
            const categoryName = String(category.name || 'Categoría');
            const categorySlug = String(category.slug || '');

            const card = document.createElement('div');
            card.className = 'categoria-card';
            card.dataset.category = categorySlug;
            card.dataset.categoryType = isSubcategory ? 'subcategory' : 'category';

            const iconWrap = document.createElement('div');
            iconWrap.className = 'categoria-icon';
            iconWrap.style.background = gradient;
            const ii = document.createElement('i');
            ii.className = icon;
            iconWrap.appendChild(ii);

            const nameEl = document.createElement('div');
            nameEl.className = 'categoria-nombre';
            nameEl.textContent = categoryName;

            const countEl = document.createElement('div');
            countEl.className = 'categoria-count';
            countEl.textContent = `${count} ${parseInt(count, 10) === 1 ? 'negocio' : 'negocios'}`;

            card.appendChild(iconWrap);
            card.appendChild(nameEl);
            card.appendChild(countEl);

            // Link sutil "📍 Cerca de ti"
            if (navigator.geolocation) {
              const nearLink = document.createElement('span');
              nearLink.className = 'category-nearby-link';
              nearLink.innerHTML = '<i class="fas fa-location-dot"></i> Cerca de ti';
              nearLink.title = `Buscar ${categoryName} cerca de tu ubicación`;
              nearLink.addEventListener('click', (ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                navigator.geolocation.getCurrentPosition((pos) => {
                  const payload = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    catName: categoryName,
                    radius: 10
                  };
                  if (isSubcategory) payload.subcategory = categorySlug;
                  else payload.category = categorySlug;
                  sessionStorage.setItem('pya_nearby_pending', JSON.stringify(payload));
                  window.location.href = '/categories.html';
                }, () => { window.location.href = '/categories.html'; });
              });
              card.appendChild(nearLink);
            }

            groupEl.appendChild(card);
        });
        frag.appendChild(groupEl);
    });
    container.appendChild(frag);

    try {
        if (!container.__pyaCategoryBound) {
            container.__pyaCategoryBound = true;
            container.addEventListener('click', (e) => {
                const card = e.target && e.target.closest ? e.target.closest('.categoria-card[data-category]') : null;
                if (!card) return;
                const categorySlug = card.dataset.category;
                const type = card.dataset.categoryType || 'category';
                if (categorySlug) {
                    const param = type === 'subcategory' ? 'subcategory' : 'category';
                    window.location.href = `categories.html?${param}=${encodeURIComponent(categorySlug)}`;
                }
            });
        }
    } catch (e) { /* noop */ }
}

function addCategoryCardListeners() {
    document.querySelectorAll('.categoria-card').forEach(card => {
        card.addEventListener('click', function() {
            const categorySlug = this.getAttribute('data-category');
            if (categorySlug) {
                window.location.href = `categories.html?category=${encodeURIComponent(categorySlug)}`;
            }
        });
    });
}

function showNoCategories(container) {
    if (!container) return;
    container.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'no-categories-message';
    const icon = document.createElement('i');
    icon.className = 'fas fa-info-circle';
    const p = document.createElement('p');
    p.textContent = 'No se encontraron categorías disponibles en este momento.';
    const btn = document.createElement('button');
    btn.className = 'btn-retry';
    btn.type = 'button';
    const bi = document.createElement('i');
    bi.className = 'fas fa-sync-alt';
    btn.appendChild(bi);
    btn.appendChild(document.createTextNode(' Reintentar'));
    btn.addEventListener('click', () => { if (typeof window.retryLoadCategories === 'function') window.retryLoadCategories(); });
    wrap.appendChild(icon);
    wrap.appendChild(p);
    wrap.appendChild(btn);
    container.appendChild(wrap);
}

function showError(container, message) {
    if (!container) return;
    container.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'error-message';
    const icon = document.createElement('i');
    icon.className = 'fas fa-exclamation-triangle';
    const p = document.createElement('p');
    p.textContent = message || 'Ocurrió un error al cargar las categorías.';
    const btn = document.createElement('button');
    btn.className = 'btn-retry';
    btn.type = 'button';
    const bi = document.createElement('i');
    bi.className = 'fas fa-sync-alt';
    btn.appendChild(bi);
    btn.appendChild(document.createTextNode(' Reintentar'));
    btn.addEventListener('click', () => { if (typeof window.retryLoadCategories === 'function') window.retryLoadCategories(); });
    wrap.appendChild(icon);
    wrap.appendChild(p);
    wrap.appendChild(btn);
    container.appendChild(wrap);
}

// Expose retry function globally
window.retryLoadCategories = function() {
    const container = document.getElementById('categorias-grid');
    if (container) {
        businessesCache = null;
        container.replaceChildren();
        const wrap = document.createElement('div');
        wrap.className = 'loading-categories';
        const sp = document.createElement('div');
        sp.className = 'spinner';
        const p = document.createElement('p');
        p.textContent = 'Reintentando conexión...';
        wrap.appendChild(sp);
        wrap.appendChild(p);
        container.appendChild(wrap);
        setTimeout(() => {
            initializeCategories(container);
            loadMainCategories(container).catch(error => {
                container.replaceChildren();
                const wrap = document.createElement('div');
                wrap.className = 'error-message';
                const icon = document.createElement('i');
                icon.className = 'fas fa-exclamation-triangle';
                const h3 = document.createElement('h3');
                h3.textContent = 'Error al cargar los datos';
                const p1 = document.createElement('p');
                p1.textContent = 'No se pudieron cargar las categorías. Por favor, verifica tu conexión a Internet.';
                const p2 = document.createElement('p');
                const small = document.createElement('small');
                small.textContent = (error && error.message) ? String(error.message) : '';
                p2.appendChild(small);
                const btn = document.createElement('button');
                btn.className = 'retry-button';
                btn.type = 'button';
                const bi = document.createElement('i');
                bi.className = 'fas fa-sync-alt';
                btn.appendChild(bi);
                btn.appendChild(document.createTextNode(' Reintentar de nuevo'));
                btn.addEventListener('click', () => { if (typeof window.retryLoadCategories === 'function') window.retryLoadCategories(); });
                wrap.appendChild(icon);
                wrap.appendChild(h3);
                wrap.appendChild(p1);
                wrap.appendChild(p2);
                wrap.appendChild(btn);
                container.appendChild(wrap);
            });
        }, 500);
    }
};

function getCategoryIcon(categoryName) {
    if (!categoryName) return 'fas fa-store';
    
    const lowerName = categoryName.toLowerCase();
    
    // Detección especial para Eventos Bodas antes del mapa
    if (lowerName.includes('evento') && (lowerName.includes('boda') || lowerName.includes('15'))) {
        return 'fas fa-heart';
    }
    
    const iconMap = {
        // Comercio y Retail
        'comercio': 'fas fa-shopping-cart',
        'compras': 'fas fa-shopping-bag',
        'tienda': 'fas fa-store-alt',
        
        // Inmobiliaria
        'inmobiliaria': 'fas fa-building',
        'propiedades': 'fas fa-building',
        'bienes raices': 'fas fa-city',
        
        // Agricultura
        'agricultura': 'fas fa-seedling',
        'agro': 'fas fa-tractor',
        'campo': 'fas fa-leaf',
        
        // Influencers y Creadores
        'influencers': 'fas fa-users-rays',
        'creadores': 'fas fa-users-rays',
        'influencer': 'fas fa-bullhorn',
        'youtuber': 'fas fa-video',
        'streamer': 'fas fa-podcast',
        
        // Gastronomía
        'restaurante': 'fas fa-utensils',
        'comida': 'fas fa-utensils',
        'gastronomía': 'fas fa-concierge-bell',
        'gastronomia': 'fas fa-concierge-bell',
        
        // Hospedaje y Turismo
        'hotel': 'fas fa-hotel',
        'hospedaje': 'fas fa-bed',
        'turismo': 'fas fa-plane',
        
        // Salud
        'salud': 'fas fa-heartbeat',
        'médico': 'fas fa-user-md',
        'medico': 'fas fa-user-md',
        'clínica': 'fas fa-hospital',
        'clinica': 'fas fa-hospital',
        
        // Belleza
        'belleza': 'fas fa-spa',
        'estética': 'fas fa-cut',
        'estetica': 'fas fa-cut',
        'salón': 'fas fa-scissors',
        'salon': 'fas fa-scissors',
        
        // Educación
        'educación': 'fas fa-graduation-cap',
        'educacion': 'fas fa-graduation-cap',
        'escuela': 'fas fa-school',
        'academia': 'fas fa-book-reader',
        
        // Tecnología
        'tecnología': 'fas fa-laptop-code',
        'tecnologia': 'fas fa-laptop-code',
        'software': 'fas fa-code',
        'informática': 'fas fa-microchip',
        'informatica': 'fas fa-microchip',
        
        // Automotriz
        'automotriz': 'fas fa-car',
        'auto': 'fas fa-car-side',
        'mecánica': 'fas fa-wrench',
        'mecanica': 'fas fa-wrench',
        
        // Construcción
        'construcción': 'fas fa-hard-hat',
        'construccion': 'fas fa-hard-hat',
        'hogar': 'fas fa-home',
        'plomería': 'fas fa-toolbox',
        'plomeria': 'fas fa-toolbox',
        
        // Fitness y Deporte
        'fitness': 'fas fa-dumbbell',
        'gimnasio': 'fas fa-dumbbell',
        'deporte': 'fas fa-running',
        'yoga': 'fas fa-spa',
        
        // Entretenimiento y Eventos
        'entretenimiento': 'fas fa-music',
        'eventos bodas, 15 años': 'fas fa-heart',
        'eventos bodas': 'fas fa-heart',
        'eventos, bodas': 'fas fa-heart',
        'bodas, 15': 'fas fa-heart',
        'eventos': 'fas fa-calendar',
        'eventos-bodas': 'fas fa-heart',
        'bodas': 'fas fa-heart',
        '15 años': 'fas fa-crown',
        'quinceañeras': 'fas fa-crown',
        'quinceaneras': 'fas fa-crown',
        'música': 'fas fa-music',
        'musica': 'fas fa-music',
        
        // Fotografía
        'fotografía': 'fas fa-camera',
        'fotografia': 'fas fa-camera',
        'foto': 'fas fa-camera-retro',
        
        // Servicios
        'servicio': 'fas fa-tools',
        'servicios': 'fas fa-tools',
        'profesional': 'fas fa-user-tie',
        
        // Medios y Publicidad
        'medios': 'fas fa-rectangle-ad',
        'publicidad': 'fas fa-rectangle-ad',
        'medios y publicidad': 'fas fa-rectangle-ad',
        
        // Mascotas
        'mascota': 'fas fa-paw',
        'mascotas': 'fas fa-paw',
        'veterinaria': 'fas fa-stethoscope',
        
        // Moda
        'moda': 'fas fa-tshirt',
        'ropa': 'fas fa-tshirt',
        'boutique': 'fas fa-shopping-bag',
        
        // Bebidas y Bar
        'bebida': 'fas fa-wine-glass',
        'bar': 'fas fa-cocktail',
        'café': 'fas fa-coffee',
        'cafe': 'fas fa-coffee',
        
        // Panadería
        'panadería': 'fas fa-bread-slice',
        'panaderia': 'fas fa-bread-slice',
        'pastelería': 'fas fa-birthday-cake',
        'pasteleria': 'fas fa-birthday-cake',
        
        // Frutas y Verduras
        'fruta': 'fas fa-apple-alt',
        'verdura': 'fas fa-carrot',
        'mercado': 'fas fa-store',
        
        // Transporte
        'transporte': 'fas fa-truck',
        'taxi': 'fas fa-taxi',
        'logística': 'fas fa-shipping-fast',
        'logistica': 'fas fa-shipping-fast',
        
        // Arte y Cultura
        'arte': 'fas fa-palette',
        'cultura': 'fas fa-theater-masks',
        'artesanía': 'fas fa-paint-brush',
        'artesania': 'fas fa-paint-brush',

        // ── Subcategorías reales de la DB ──
        'farmacias': 'fas fa-prescription-bottle',
        'farmacia': 'fas fa-prescription-bottle',
        'peluquerias': 'fas fa-scissors',
        'peluqueria': 'fas fa-scissors',
        'barberia': 'fas fa-cut',
        'barberías': 'fas fa-cut',
        'barberias': 'fas fa-cut',
        'dentistas': 'fas fa-tooth',
        'dentista': 'fas fa-tooth',
        'psicologos': 'fas fa-brain',
        'psicologo': 'fas fa-brain',
        'psicólogos': 'fas fa-brain',
        'abogados': 'fas fa-gavel',
        'abogado': 'fas fa-gavel',
        'contadores': 'fas fa-calculator',
        'contador': 'fas fa-calculator',
        'veterinarias': 'fas fa-paw',
        'comida-rapida': 'fas fa-hamburger',
        'cafeterias': 'fas fa-coffee',
        'cafetería': 'fas fa-coffee',
        'cafeteria': 'fas fa-coffee',
        'medicos-generales': 'fas fa-user-md',
        'especialistas': 'fas fa-stethoscope',
        'restaurantes': 'fas fa-utensils',
        'spas': 'fas fa-spa',
        'centros-estetica': 'fas fa-spa',
        'maquillador': 'fas fa-paint-brush',
        'maquilladores': 'fas fa-paint-brush',
        'estudios-grabacion': 'fas fa-microphone',
        'dj': 'fas fa-headphones',
        'fotografia': 'fas fa-camera',
        'consultores': 'fas fa-chart-bar',
        'electricistas': 'fas fa-bolt',
        'plomeros': 'fas fa-wrench',
        'constructoras': 'fas fa-hard-hat',
        'tiendas': 'fas fa-store-alt',
        'gimnasios': 'fas fa-dumbbell',
        'escuelas': 'fas fa-school',
        'academias': 'fas fa-book-reader',
        'tutorias': 'fas fa-chalkboard-teacher',
        'desarrollo-web': 'fas fa-code',
        'desarrollo web': 'fas fa-code',
        'programacion': 'fas fa-code',
        'productores': 'fas fa-film',
        'productor': 'fas fa-film',
        'floristeria': 'fas fa-seedling',
        'floristerias': 'fas fa-seedling',
        'flores': 'fas fa-fan'
    };

    // Primero buscar coincidencia exacta
    if (iconMap[lowerName]) {
        return iconMap[lowerName];
    }

    // Luego buscar coincidencias parciales (ordenadas por longitud para priorizar las más específicas)
    const sortedKeys = Object.keys(iconMap).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
        if (lowerName.includes(key)) {
            return iconMap[key];
        }
    }

    return 'fas fa-store';
}
